<script setup lang="ts">
    import { useRoute } from 'vue-router';
    import { computed, ref } from 'vue';
    import DateRangePicker from '@shared/components/DateRangePicker.vue';
    import BSDropdownButton from '@shared/components/BSDropdownButton.vue';
    import FormSelect from '@shared/components/Form/FormSelect.vue';
    import type { Issue } from '../../interfaces';
    import { getIssueStatisticsContactListHeadings } from './statistics-types';
    import type { StatisticsPresetFilter } from './statistics-types';
    import StatisticsContactFilter from '@views/Lists/components/ContactFilter.vue';
    import type { SerializedContactFilterState } from '@views/Lists/components/ContactFilter.vue';
    import type { AdditionalField } from '@views/Lists/ListsTypes.d';

    const props = defineProps<{
        issue: Issue | null;
        dateRange: { start: string | null; end: string | null };
        selectedVariationFilter: string | null;
        showContactList: boolean;
        contactType?: string | null;
        canScheduleRecurring: boolean;
        hasIssueVariations: boolean;
        availableFields: AdditionalField[];
    }>();

    const contactListHeading = getIssueStatisticsContactListHeadings();

    const emit = defineEmits<{
        (e: 'update:dateRange', start: string | null, end: string | null): void;
        (e: 'update:selectedVariationFilter', filter: string | null): void;
        (e: 'createCopy'): void;
        (e: 'openScheduleReminder'): void;
        (e: 'openRecurringEditor'): void;
        (e: 'update:contactFilter', filter: string): void;
        (e: 'update:contactFilterState', state: Partial<SerializedContactFilterState>): void;
    }>();

    const contactFilterRef = ref<InstanceType<typeof StatisticsContactFilter>>();

    function applyPreset(label: string, presets: StatisticsPresetFilter[]) {
        contactFilterRef.value?.setPresetFilters(presets);
    }

    defineExpose({ contactFilterRef, applyPreset });

    const route = useRoute();

    const dateQuery = computed(() => {
        const q: Record<string, string> = {};
        if (route.query.start) q.start = route.query.start as string;
        if (route.query.end) q.end = route.query.end as string;
        if (route.query.contactFilter) q.contactFilter = route.query.contactFilter as string;
        if (route.query.contactFilterState) q.contactFilterState = route.query.contactFilterState as string;
        return q;
    });

    function onDateRangeChange(start: string, end: string) {
        emit('update:dateRange', start || null, end || null);
    }

    function onVariationChange(val: string | undefined) {
        emit('update:selectedVariationFilter', val || null);
    }

    function print() {
        window.print();
    }
</script>

<template>
    <nav class="navbar main-navbar" role="navigation">
        <div class="container-fluid">
            <p class="navbar-title">
                <router-link :to="{ name: 'Issues', query: { tab: 'sent' } }">{{ $Resources.IssuesMenuItem }}</router-link>
                <span class="fw-400 c-light m-xs-l">/</span>
                <template v-if="props.showContactList">
                    <router-link class="m-xs-l" :to="{ name: 'IssueStatistics', params: { issueId: route.params.issueId }, query: dateQuery }">
                        {{ $Resources.IssueStatisticsBreadcrumb }}
                    </router-link>
                    <span class="fw-400 c-light m-xs-l">/</span>
                    {{ contactType ? (contactListHeading[contactType] ?? contactType) : '' }}
                </template>
                <template v-else>
                    <span class="m-xs-l">
                        {{ $Resources.IssueStatisticsBreadcrumb }}
                    </span>
                </template>
            </p>

            <div v-show="!props.showContactList" id="teleported-target-tabs" class="m-auto-r"></div>

            <div v-show="!props.showContactList">
                <ul class="list-unstyled nav navbar-nav">
                    <li>
                        <DateRangePicker :dateFilter="{ start: dateRange.start, end: dateRange.end }" @onDateRangeChange="onDateRangeChange" />
                    </li>

                    <li>
                        <BSDropdownButton menuClass="dropdown-menu-right">
                            <template #toggle>
                                <span class="glyphicon glyphicon-filter m-xxs-r"></span>
                                <span class="navbar-label">{{ $Resources.FilterHeading }}</span>
                            </template>
                            <template #content>
                                <li class="dropdown-menu-content" :class="(contactFilterRef?.filterCount || selectedVariationFilter) && 'has-selected'">
                                    <StatisticsContactFilter
                                        ref="contactFilterRef"
                                        :availableFields="availableFields"
                                        :scalarOnly="true"
                                        :show-tags="true"
                                        @change="(f) => emit('update:contactFilter', f)"
                                        @update:filterState="(s) => emit('update:contactFilterState', s)" />

                                    <div class="row m-sm-t" v-if="hasIssueVariations">
                                        <div class="col-sm-6">
                                            <FormSelect
                                                :label="$Resources.ABTestFilterheading"
                                                :modelValue="selectedVariationFilter ?? ''"
                                                :options="
                                                    issue!.IssueVariationConfiguration!.IssueVariations.map((v) => ({
                                                        label: v.AlternativeSubject,
                                                        value: String(v.IssueVariationId),
                                                    }))
                                                "
                                                :emptyOption="true"
                                                @update:modelValue="onVariationChange" />
                                        </div>
                                    </div>
                                </li>
                            </template>
                        </BSDropdownButton>
                    </li>

                    <li v-if="issue && !issue.Event && !issue.Survey">
                        <button class="btn btn-default" @click="emit('openScheduleReminder')">
                            <span class="glyphicon glyphicon-bell m-xxs-r"></span>
                            {{ $Resources.ReminderModelOpenReminder }}
                        </button>
                    </li>

                    <li>
                        <BSDropdownButton menuClass="dropdown-menu-right">
                            <template #toggle>
                                <span class="glyphicon glyphicon-option-vertical m-xxs-r"></span>
                                {{ $Resources.NavbarMoreButton }}
                            </template>
                            <template #content>
                                <li v-if="canScheduleRecurring">
                                    <a href="javascript:void(0)" role="button" @click="emit('openRecurringEditor')">
                                        <span class="glyphicon glyphicon-repeat m-xxs-r"></span>
                                        {{ $Resources.StatisticsNavbarTabLabelSchedule }}
                                    </a>
                                </li>
                                <li v-if="issue">
                                    <a :href="`/Issues/${issue.IssueId}`">
                                        <span class="glyphicon glyphicon-edit"></span>
                                        {{ $Resources.EditIssueButton }}
                                    </a>
                                </li>
                                <li>
                                    <a href="javascript:void(0)" role="button" @click="emit('createCopy')">
                                        <span class="glyphicon glyphicon-duplicate"></span>
                                        {{ $Resources.SurveyCloneButton }}
                                    </a>
                                </li>
                                <li>
                                    <a href="javascript:void(0)" role="button" @click="print()">
                                        <span class="glyphicon glyphicon-print"></span>
                                        {{ $Resources.PrintReportBtn }}
                                    </a>
                                </li>
                            </template>
                        </BSDropdownButton>
                    </li>
                </ul>
            </div>
        </div>
    </nav>
</template>
